import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ToastMessageOptions, SelectItem } from 'primeng/api';
import { ConfirmationService } from 'primeng/api';

import { 
         UserAuthorizationService,
         IApplicationContextService,
         IOpsViewerMessagesService } from '../../../services/services';
import { Title } from '@angular/platform-browser';

import { Subscription } from 'rxjs';
import { IUserManagerService } from '../../../services/iUserManager.service';
import { Company, CompanyClient, CompanyNameViewModel, Device, DeviceClient, User } from '../../../services/nswag/nswagclient';
import { DeviceCategories } from '../../../models/deviceCategories';

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'device-list',
    templateUrl: './device-list.component.html',
    styleUrls: ['./device-list.component.css'],
    standalone: false
})
export class DeviceListComponent implements OnInit, OnDestroy {

    cols: any[] = [];
    devicesReady = false;
    devices: Device[] = [];
    rawDevices: Device[] = []; // Store original devices before adding computed fields
    messages: ToastMessageOptions[] = [];
    deviceCategories: SelectItem[] = [];
    deletedFilterOptions: SelectItem[] = [];
    companies: Company[] = [];
    companiesReady = false;
    isCompanyAdministrationEnabled = false;
    user: User | null = null;
    isUserReady = false;
    everythingReady = false;
    showDeleted = false;

    private _companyContextSubscription?: Subscription;

    constructor(
        private _deviceService: DeviceClient,
        private _companyService: CompanyClient,
        private _userAuthorizationService: UserAuthorizationService,
        private _userManagerService: IUserManagerService,
        private _router: Router,
        private _confirmationService: ConfirmationService,
        private _titleService: Title,
        private _applicationContextService: IApplicationContextService,
        private _messageService: IOpsViewerMessagesService) { }

    ngOnInit() {
        this.updateDeviceList();
        this.setPageTitle();
        this.setColumns();
        this.getDevices();
        this.getCompanies();
        this.getAuthenticatedUser();
        this.listenForCompanyContext();
    }

    ngOnDestroy() {
        this._companyContextSubscription?.unsubscribe();
    }

    setPageTitle() {
        this._titleService.setTitle('OpsViewer Devices');
    }

    setColumns() {
        this.cols = [
            { field: 'id', header: 'Id', width: '325px', filterMatchMode: 'contains' },
            { field: 'name', header: 'Name', width: '250px', filterMatchMode: 'contains' },
            { field: 'category', header: 'Category', width: '150px', filterMatchMode: 'in' },
            { field: 'companyName', header: 'Company', width: '150px', filterMatchMode: 'contains' },
            { field: 'deletedStatus', header: 'Deleted', width: '120px', filterMatchMode: 'equals' },
            { field: 'activeStatus', header: 'Active', width: '100px', filterMatchMode: 'contains' },
            { field: '', header: '', width: '60px' }
        ];

        this.deviceCategories = [];
        this.deviceCategories.push({ label: DeviceCategories.DPU, value: DeviceCategories.DPU });
        this.deviceCategories.push({ label: DeviceCategories.Ultralight, value: DeviceCategories.Ultralight });
        this.deviceCategories.push({ label: DeviceCategories.VisionDesktop, value: DeviceCategories.VisionDesktop });
        this.deviceCategories.push({ label: DeviceCategories.FlowBack, value: DeviceCategories.FlowBack });
        this.deviceCategories.push({ label: DeviceCategories.TCP, value: DeviceCategories.TCP });

        this.deletedFilterOptions = [];
        this.deletedFilterOptions.push({ label: 'Yes', value: 'Yes' });
        this.deletedFilterOptions.push({ label: 'No', value: 'No' });
        
    }

    getIsActive(device: Device){
        if (device.category !== DeviceCategories.TCP) return "";
        return device.tcpDeviceConfiguration?.active ? "yes" : "no";
    }
    getDevices() {
        this.messages = [];
        this.devicesReady = false;
        this.everythingReady = false;
        this._deviceService.get(this.showDeleted).subscribe(
            result => {
                this.rawDevices = result ?? [];
                this.devicesReady = true;
                this.updateDeviceListWithCompanyNames();
                this.checkIfEverythingReady();
            },
            error => {
                this.devicesReady = true;
                this.rawDevices = [];
                this.devices = [];
                this.checkIfEverythingReady();
                this._messageService.errorMessage(error, 'Could not Get Devices');
            }
        );
    }

    getCompanies() {
        this._companyService.get().subscribe(
            results => {
                if (results) {
                    this.companies = results;
                }
                this.companiesReady = true;
                this.updateDeviceListWithCompanyNames();
                this.checkIfEverythingReady();
            },
            error => { this._messageService.errorMessage(error, 'Could not Get Companies'); }
        );
    }

 

      getAuthenticatedUser() {

        this._userManagerService.getCurrentUser().subscribe(u=>{
          this.user = u;
          this.isUserReady = true;
          this.checkIfEverythingReady();
        });
      }
 

  listenForCompanyContext() {
    // Initialize
    this.updateDeviceList();

    // Listen for changes to selected company
    this._companyContextSubscription = this._applicationContextService.companyContextObserver.subscribe(
        c => this.updateDeviceList(c));
  }

  updateDeviceList(company: CompanyNameViewModel | null = null) {
    if (this.isCompanyAdministrationEnabled) {
      // If not passed in attempt to get current value
      if (!company) {
        company = this._applicationContextService.getCompanyContext();
      }
      const companyId = company?.companyId;
      // Filter list to selected company
      if (companyId && this.devices) {
        this.devices = this.devices.filter(d => d.companyId === companyId);
      }
    }
  }

    isAdmin(device: Device): boolean {
        if (!this.user) {
            return false;
        }
        if (this.isCompanyAdministrationEnabled) {
            if (!device.companyId) {
                return false;
            }
            return this._userAuthorizationService.isUserAdmin(this.user, device.companyId, false);
        } else {
            return this._userAuthorizationService.isUserAdmin(this.user, '', false);
        }
    }

    checkIfEverythingReady() {
        this.everythingReady = this.devicesReady && this.isUserReady && this.companiesReady;
    }

    navigateToAddDevice() {
        this._router.navigate(['/device/' + encodeURIComponent('new')]);
    }

    navigateToDiagnostics() {
        this._router.navigate(['/device-diagnostics']);
    }

    navigateToDetail(deviceId: string) {
        this._router.navigate(['/device/' + encodeURIComponent(deviceId)]);
    }

    refresh() {
        this.cols = [];
        this.devicesReady = false;
        this.devices = [];
        this.messages = [];
        this.ngOnInit();
    }

    confirmDelete(device: Device) {
        if (!device.id) {
            return;
        }
        this._confirmationService.confirm(
            {
                key: 'confirmationDialog',
                header: 'Delete Confirmation',
                message: 'Are you sure you want to delete ' + device.name + '?',
                icon: 'fa fa-question-circle',
                accept: () => { this.deleteDevice(device.id); }// ,
                // reject: () => { }
            }
        );
    }

    deleteDevice(deviceId: string | undefined) {
        if (!deviceId) {
            return;
        }
        this._deviceService.delete(deviceId).subscribe(
            result => {
                this.refresh();
                this._messageService.infoMessage('Device has been deleted', '');
            },
            error => { this._messageService.errorMessage(error, 'Could not Delete Device'); }
        );
    }

    confirmUndelete(device: Device) {
        if (!device.id) {
            return;
        }
        this._confirmationService.confirm(
            {
                key: 'confirmationDialog',
                header: 'Restore Confirmation',
                message: 'Are you sure you want to restore ' + device.name + '?',
                icon: 'fa fa-question-circle',
                accept: () => { this.undeleteDevice(device.id); }
            }
        );
    }

    undeleteDevice(deviceId: string | undefined) {
        if (!deviceId) {
            return;
        }
        this._deviceService.undelete(deviceId).subscribe(
            () => {
                this.refresh();
                this._messageService.infoMessage('Device has been restored', '');
            },
            error => { this._messageService.errorMessage(error, 'Could not Restore Device'); }
        );
    }

    updateDeviceListWithCompanyNames() {
        if (!this.devicesReady || !this.companiesReady) return;
        this.devices = this.rawDevices.map(device => {
            (device as any).companyName = this.getCompanyName(device.companyId);
            (device as any).activeStatus = this.getIsActive(device);
            (device as any).deletedStatus = device.isDeleted ? 'Yes' : 'No';
            return device;
        });
        this.updateDeviceList();
    }

    getCompanyName(companyId: string | undefined): string {
        if (!this.companies || !companyId) return '';
        const foundCompany = this.companies.find(c => c.id === companyId);
        if (foundCompany) {
            return foundCompany.name ?? '';
        }
        return '';
    }

    handleError(error: any, displayMessage: string) {
        this._messageService.errorMessage(error, displayMessage);
    }

    onShowDeletedChange() {
        this.getDevices();
    }

    asMessageSeverity(severity: ToastMessageOptions['severity'] | undefined) {
        return severity as 'success' | 'info' | 'warn' | 'error' | 'secondary' | 'contrast' | undefined;
    }
}
